// client/src/pages/ShopPOS/hooks/usePOSInventory.ts
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getShopInventoryWithCart, getProducts } from "@/lib/api";
import { indexedDBService } from "@/lib/db/indexed-db";
import { useOfflineContext } from "@/lib/contexts/offline-context";

interface UsePOSInventoryProps {
  currentShop: any;
  // isOfflineMode ya no se recibe como prop, viene del contexto
}

export function usePOSInventory({ currentShop }: UsePOSInventoryProps) {
  const { isOfflineMode } = useOfflineContext(); // ← OBTENER DEL CONTEXTO
  const queryClient = useQueryClient();

  // Estados de paginación y filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [minPrice, setMinPrice] = useState<number | "">("");
  const [maxPrice, setMaxPrice] = useState<number | "">("");

  // Estado para mantener items del carrito entre recargas
  const [persistedCartItems, setPersistedCartItems] = useState<any[]>([]);

  // {NUEVO: Estado para guardar el inventario COMPLETO (sin filtros)}
  const [fullInventory, setFullInventory] = useState<any[]>([]);

  // Query para obtener inventario con carrito (filtrado)
  const {
    data: inventoryResponse,
    isLoading: inventoryLoading,
    refetch: refetchInventory,
  } = useQuery({
    queryKey: [
      "shopInventoryWithCart",
      currentShop?.id,
      currentPage,
      itemsPerPage,
      search,
      minPrice,
      maxPrice,
    ],
    queryFn: async () => {
      if (!currentShop) {
        return { filtered: { data: [], total: 0 }, cartItems: [] };
      }

      if (isOfflineMode) {
        console.log(
          "📴 [POS] Modo offline - Cargando inventario desde IndexedDB",
        );
        const offlineInventory = await indexedDBService.getOfflineInventory(
          currentShop.id,
        );
        console.log("📦 [DEBUG] offlineInventory:", offlineInventory?.length);

        if (offlineInventory && offlineInventory.length > 0) {
          // Actualizar fullInventory con los datos offline
          setFullInventory(offlineInventory);
          let filtered = offlineInventory.filter((item: any) => item.stock > 0);

          if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter((item: any) =>
              item.product?.name?.toLowerCase().includes(searchLower),
            );
          }

          if (minPrice !== "") {
            filtered = filtered.filter(
              (item: any) => Number(item.price) >= Number(minPrice),
            );
          }

          if (maxPrice !== "") {
            filtered = filtered.filter(
              (item: any) => Number(item.price) <= Number(maxPrice),
            );
          }

          const start = (currentPage - 1) * itemsPerPage;
          const end = start + itemsPerPage;
          const paginatedData = filtered.slice(start, end);

          console.log(
            `📦 [POS] Offline: ${filtered.length} productos, 0 en carrito`,
          );

          return {
            filtered: {
              data: paginatedData,
              total: filtered.length,
              page: currentPage,
              limit: itemsPerPage,
              totalPages: Math.ceil(filtered.length / itemsPerPage),
            },
            cartItems: [],
          };
        }

        console.log("📭 [POS] No hay datos offline");
        return { filtered: { data: [], total: 0 }, cartItems: [] };
      }

      // Online: usar API
      console.log("🌐 [POS] Modo online - Cargando desde API");
      return await getShopInventoryWithCart(currentShop.id, [], {
        page: currentPage,
        limit: itemsPerPage,
        search: search,
        minPrice: minPrice !== "" ? minPrice : undefined,
        maxPrice: maxPrice !== "" ? maxPrice : undefined,
      });
    },
    enabled: !!currentShop,
    refetchOnWindowFocus: !isOfflineMode,
    refetchInterval: isOfflineMode ? false : 30000,
    staleTime: 10000,
  });

  // {NUEVO: Cargar el inventario COMPLETO sin filtros al iniciar}
  useEffect(() => {
    const loadFullInventory = async () => {
      if (!currentShop) return;

      // {NUEVO: También cargar offline}
      if (isOfflineMode) {
        const offlineInventory = await indexedDBService.getOfflineInventory(
          currentShop.id,
        );
        if (offlineInventory) {
          setFullInventory(offlineInventory);
          console.log(
            "📦 [POS] fullInventory cargado offline:",
            offlineInventory.length,
          );
        }
        return;
      }

      // Online: cargar desde API
      console.log("📦 [POS] Cargando inventario completo sin filtros...");
      const result = await getShopInventoryWithCart(currentShop.id, [], {
        page: 1,
        limit: 1000,
        search: "",
      });

      if (result.filtered?.data) {
        setFullInventory(result.filtered.data);
        console.log(
          `✅ [POS] Inventario completo cargado: ${result.filtered.data.length} productos`,
        );
      }
    };

    loadFullInventory();
  }, [currentShop?.id, isOfflineMode]);

  // Query para obtener productos
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  // Extraer datos de la respuesta
  const inventoryData = inventoryResponse?.filtered;
  const cartInventoryItems = inventoryResponse?.cartItems || [];

  // Actualizar estado persistente cuando hay datos nuevos
  useEffect(() => {
    if (cartInventoryItems.length > 0) {
      setPersistedCartItems(cartInventoryItems);
    }
  }, [cartInventoryItems]);

  // Usar items persistidos durante la carga
  const displayCartItems =
    cartInventoryItems.length > 0 ? cartInventoryItems : persistedCartItems;

  // Inventario para la grilla
  const inventory = inventoryData?.data || [];
  const inventoryTotal = inventoryData?.total || 0;

  // Filtrar elementos inválidos
  const filteredInventory = inventory.filter((i: any) => {
    if (!i || !i.productId || !i.product) return false;
    return i.stock > 0;
  });

  // Actualizar total de páginas
  useEffect(() => {
    if (inventoryData?.total) {
      setTotalItems(inventoryData.total);
      setTotalPages(Math.ceil(inventoryData.total / itemsPerPage));
    }
  }, [inventoryData?.total, itemsPerPage]);

  // Función para refrescar inventario
  const refreshInventory = () => {
    queryClient.invalidateQueries({
      queryKey: ["shopInventoryWithCart", currentShop?.id],
    });
    queryClient.refetchQueries({
      queryKey: ["shopInventoryWithCart", currentShop?.id],
    });
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearch("");
    setMinPrice("");
    setMaxPrice("");
    setCurrentPage(1);
  };

  // Guardar inventario para modo offline
  const saveInventoryForOffline = async () => {
    if (fullInventory.length > 0 && !isOfflineMode && currentShop) {
      await indexedDBService.saveFullInventoryForOffline(
        currentShop.id,
        fullInventory,
      );
      console.log("📦 [POS] Inventario guardado para modo offline");
    }
  };

  return {
    // Estados
    currentPage,
    itemsPerPage,
    totalItems,
    totalPages,
    search,
    minPrice,
    maxPrice,
    // Datos
    inventory,
    inventoryData,
    inventoryTotal,
    filteredInventory,
    displayCartItems,
    fullInventory, // ← NUEVO: Inventario completo sin filtros
    products,
    // Loading
    inventoryLoading,
    productsLoading,
    isLoading: inventoryLoading || productsLoading,
    // Setters
    setCurrentPage,
    setItemsPerPage,
    setSearch,
    setMinPrice,
    setMaxPrice,
    // Funciones
    refetchInventory,
    refreshInventory,
    clearFilters,
    saveInventoryForOffline,
  };
}
